/**
 * ObjectList React component
 * 
 * @author    Maurizio Tidei, Jens Gruschel
 * @copyright © 2018 contexagon GmbH
 */


// react
import React from "react";
import PropTypes from 'prop-types';

// material UI
import {withStyles} from '@material-ui/core/styles';
import {fade} from '@material-ui/core/styles/colorManipulator';
import BottomNavigationAction from "@material-ui/core/BottomNavigationAction/BottomNavigationAction";
import BottomNavigation from "@material-ui/core/BottomNavigation/BottomNavigation";
import Input from '@material-ui/core/Input';
import IconButton from "@material-ui/core/IconButton";

// icons
import ViewModuleIcon from '@material-ui/icons/ViewModule';
import ViewHeadlineIcon from '@material-ui/icons/ViewHeadline';
import FindInPageIcon from '@material-ui/icons/FindInPageTwoTone';
import AddCircleIcon from '@material-ui/icons/AddCircle';

// application
import ObjectGrid from "./ObjectGrid";
import ObjectTable from "./ObjectTable";
import { Typography } from "@material-ui/core";


// regular expressions for filter
const escapeRegExp = new RegExp("([\\.\\+\\?\\(\\)\\[\\]\\{\\}\\^\\$\\\\])", "g");
const wildcardRegExp = new RegExp("[\\*]+", "g");


function testItem(item, expression) {
    if (expression.test(item.title)) return true;
    if (item.tags) {
        for (const tag of item.tags) {
            if (expression.test(tag)) return true;
        }
    }
    return false;
}


/**
 * ObjectList wraps the grid and table view components for displaying a list of objects
 *
 * @author Maurizio Tidei, Jens Gruschel
 */
export class ObjectList extends React.Component {

    state = {
        filterPhrase: ''
    };

    componentDidUpdate(prevProps) {
        if (this.props.items !== prevProps.items && this.state.filterPhrase.length > 0) {
            this.setState({filterPhrase: ''});
        }
    }

    render() {

        const {classes} = this.props;

        const items = this.filter(this.props.items);

        let list = null;
        if (this.props.representation === "table") {
            list = <ObjectTable
                    items={items}
                    readOnly={this.props.readOnly}
                    onSelect={this.props.onSelect}
                    onDelete={this.props.onDelete}
                    onUndelete={this.props.onUndelete}
                    onEdit={this.props.onEdit}
                    onDuplicate={this.props.onDuplicate}
                    onAdd={this.props.onAdd}
                    onSearchTag={this.props.onSearchTag}
                    pagination={true}
                />;
        }
        else {
            list = <ObjectGrid
                    items={items}
                    readOnly={this.props.readOnly}
                    onSelect={this.props.onSelect}
                    onDelete={this.props.onDelete}
                    onUndelete={this.props.onUndelete}
                    onEdit={this.props.onEdit}
                    onDuplicate={this.props.onDuplicate}
                    onAdd={items.length === 0 ? this.props.onAdd : undefined}
                    onSearchTag={this.props.onSearchTag}
                    pagination={true}
                />;
        }

        return (
            <div>
                <div className={classes.listBar}>
                    <BottomNavigation showLabels value={this.props.representation} onChange={(event, value) => this.props.onChangeRepresentation(value)} className={classes.navigationRoot}>
                        <BottomNavigationAction label="Kacheln" value="grid" icon={<ViewModuleIcon />} classes={{root: classes.navigationButton, selected: classes.navigationButtonSelected}}/>
                        <BottomNavigationAction label="Tabelle" value="table" icon={<ViewHeadlineIcon />} classes={{root: classes.navigationButton, selected: classes.navigationButtonSelected}}/>
                    </BottomNavigation>
                    <div className={classes.search}>
                        <div className={classes.searchIcon}>
                            <FindInPageIcon/>
                        </div>
                        <Input
                            id={"searchInput"}
                            value={this.state.filterPhrase}
                            placeholder="Filtern…"
                            disableUnderline
                            autoFocus={true}
                            classes={{
                                root: classes.inputRoot,
                                input: classes.inputInput,
                            }}
                            onChange={event => this.setState({filterPhrase: event.target.value})}
                        />
                        <div className={classes.itemCount}>
                            <Typography component="p">{"(" + items.length + ")"}</Typography>
                        </div>
                    </div>
                    {this.props.onAdd && !this.props.readOnly ?
                        <IconButton onClick={this.props.onAdd}>
                            <AddCircleIcon/>
                        </IconButton> : null
                    }
                </div>
                {list}
            </div>
        );
    };

    filter(items) {

        // split string into separate phrases
        const phrases = this.state.filterPhrase.split(" ").filter(phrase => phrase !== "");

        // turn each filter phrase into a regular expression, escaping most of the special characters and using "*" as wildcard
        const escapedPhrases = phrases.map(phrase => phrase.replace(escapeRegExp, "\\$1").replace(wildcardRegExp, ".*"));
        const expressions = escapedPhrases.map(phrase => new RegExp(phrase, "i"));

        // apply filter to items
        return items.filter(item => {
            for (const expression of expressions) {
                if (!testItem(item, expression)) return false;
            }
            return true;
        });
    }
}


ObjectList.propTypes = {
    items: PropTypes.array.isRequired,
    readOnly: PropTypes.bool,
    onSelect: PropTypes.func.isRequired,
    onEdit: PropTypes.func,
    onDelete: PropTypes.func,
    onUndelete: PropTypes.func,
    onDuplicate: PropTypes.func,
    onAdd: PropTypes.func,
    onSearchTag: PropTypes.func,
    representation: PropTypes.string,
    onChangeRepresentation: PropTypes.func
};


const styles = theme => ({
    listBar: {
        width: "100%",
        display: "flex",
        alignItems: "center"
    },
    itemCount: {
        display: "inline-flex",
        position: "absolute",
        padding: theme.spacing.unit,
        right: theme.spacing.unit,
        opacity: 0.33
    },
    navigationRoot: {
        background: "none",
        justifyContent: "left"
    },
    navigationButton: {
        paddingTop: "0 !important",
        paddingBottom: "4px !important",
        maxWidth: 100
    },
    navigationButtonSelected: {
        paddingTop: 0
    },
    root: {
        display: "normal"
    },
    thumbnail: {
        width: 50,
        height: 50,
        borderRadius: 25,
        objectFit: "cover"
    },
    tagsColumn: {
        fontStyle: "italic"
    },
    headCell: {
        paddingRight: 4
    },
    search: {
        position: 'relative',
        borderRadius: theme.shape.borderRadius,
        backgroundColor: fade(theme.palette.common.black, 0.05),
        '&:hover': {
            backgroundColor: fade(theme.palette.common.black, 0.15),
        },
        marginRight: theme.spacing.unit * 2,
        marginLeft: 0,
        width: '100%',
        [theme.breakpoints.up('sm')]: {
            marginLeft: theme.spacing.unit * 3,
            width: 'auto',
        },
    },
    searchIcon: {
        width: theme.spacing.unit * 9,
        height: '100%',
        position: 'absolute',
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: 0.25
    },
    inputRoot: {
        color: 'inherit',
        width: '100%',
    },
    inputInput: {
        paddingTop: theme.spacing.unit,
        paddingRight: theme.spacing.unit * 10,
        paddingBottom: theme.spacing.unit,
        paddingLeft: theme.spacing.unit * 10,
        transition: theme.transitions.create('width'),
        width: '100%',
        [theme.breakpoints.up('md')]: {
            width: 200,
        },
    }
});

export default withStyles(styles)(ObjectList);